import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';

import {FILE_KEY_DELIMITER, type Property} from '../../utilTypes';
import PropertyHeader from './PropertyHeader';

type DropdownProps = {
  property: Property;
  value: string | number | boolean | (string | number)[] | undefined | null;
  onChange: (newValue: any, actionMeta: any) => void;
};

type PropertyOption = {
  property: string;
  label?: string;
  value?: string | number | boolean;
};

export const Dropdown = ({property, value, onChange}: DropdownProps) => {
  const isArrayEnum =
    property.type === 'array' && Array.isArray(property.items?.enum);

  const id = (property.file ?? '') + FILE_KEY_DELIMITER + property.key;
  const items: any[] = (() => {
    if (property.type === 'boolean') {
      return [true, false];
    }
    if (isArrayEnum) {
      return (property.items?.enum as any[]) ?? [];
    }
    return property.enum ?? [];
  })();

  const options: PropertyOption[] = items.map(val => ({
    property: id,
    label: val != null ? val.toString() : '',
    value: val,
  }));

  if (isArrayEnum) {
    const selectedValues = Array.isArray(value)
      ? (value as (string | number)[])
      : [];
    const selectedOptions: PropertyOption[] = options.filter(opt =>
      selectedValues.some(v => v === opt.value)
    );

    const maxItems = property.maxItems ?? null;
    const isOptionDisabled = (opt: PropertyOption) => {
      if (maxItems == null) {
        return false;
      }
      const isAlreadySelected = selectedOptions.some(
        sel => sel.value === opt.value
      );
      return selectedOptions.length >= maxItems && !isAlreadySelected;
    };

    const handleChange = (
      selected: readonly PropertyOption[] | null,
      actionMeta: any
    ) => {
      const vals = (selected ?? []).map(o => o.value) as (string | number)[];
      onChange({property: id, value: vals}, actionMeta);
    };

    return (
      <div
        className="m-0 flex flex-col text-sm"
        key={property.key}
        data-test={property.key}>
        <PropertyHeader property={property} />
        <Select<PropertyOption, true>
          id={id}
          options={options}
          value={selectedOptions}
          onChange={handleChange as any}
          isMulti
          closeMenuOnSelect={false}
          size="variable"
          isOptionDisabled={isOptionDisabled}
          placeholder={
            <Tailwind>
              <span className="text-sm text-moon-500">
                {property.placeholder ?? 'Select an option'}
              </span>
            </Tailwind>
          }
        />
      </div>
    );
  }

  const optionValue: PropertyOption | null =
    value == null
      ? null
      : {
          property: id,
          label: (value as any)?.toString?.(),
          value: value as string | number | boolean,
        };

  const handleChangeSingle = (
    selected: PropertyOption | null,
    actionMeta: any
  ) => {
    onChange({property: id, value: selected?.value}, actionMeta);
  };

  return (
    <div
      className="m-0 flex flex-col text-sm"
      key={property.key}
      data-test={property.key}>
      <PropertyHeader property={property} />
      <Select<PropertyOption, false>
        id={id}
        options={options}
        value={optionValue}
        onChange={handleChangeSingle as any}
        placeholder={
          <Tailwind>
            <span className="text-sm text-moon-500">Select an option</span>
          </Tailwind>
        }
      />
    </div>
  );
};
